import { motion } from "framer-motion";
import { usePageMeta } from "@/lib/seo";
import { Target, Eye, Award } from "lucide-react";
import ScientificPattern from "@/components/ScientificPattern";

export default function About() {
  usePageMeta("About Us", "Learn about True Impact Peptides — our commitment to quality, independent testing, and Research Use Only compliance.", "/about");
  return (
    <div data-testid="about-page">
      <section className="relative py-24 bg-section-tint overflow-hidden">
        <ScientificPattern variant="hex" className="opacity-50" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-brandblue-600">About Us</span>
          <h1 className="mt-3 text-4xl sm:text-5xl tracking-tighter font-medium text-slate-900 font-display">Committed to <span className="text-gradient-blue">Scientific Excellence</span></h1>
          <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
            True Impact Peptides was founded to serve the research community with Research Use Only compounds that meet the highest standards of purity, documentation, and laboratory transparency.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10">
        <img src="https://images.unsplash.com/photo-1766297248076-2848d8d0d464?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Scientist in laboratory" className="rounded-3xl object-cover h-[380px] w-full" />
        <div className="flex flex-col justify-center space-y-8">
          {[
            { icon: Target, title: "Our Mission", desc: "To supply the global research community with laboratory-grade Research Use Only peptides, backed by rigorous quality systems and complete batch transparency." },
            { icon: Eye, title: "Our Vision", desc: "A research supply chain defined by scientific integrity — where every compound is traceable, every batch is documented, and every claim is verifiable." },
            { icon: Award, title: "Our Commitment", desc: "Independent third-party testing, HPLC and mass spectrometry verification, and accessible Certificates of Analysis for every product we manufacture." },
          ].map((item) => (
            <div key={item.title} className="group p-4 rounded-2xl hover:bg-blue-50/50 transition-all duration-300" data-testid={`about-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-blue-900 group-hover:to-blue-500 transition-all duration-300">
                  <item.icon className="h-4 w-4 text-brandblue-600 group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-display font-semibold text-slate-900">{item.title}</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-navy-900 text-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-medium mb-4">Laboratory Excellence, Every Batch</h2>
          <p className="text-slate-300 text-sm leading-relaxed">
            Every compound we manufacture undergoes independent verification before it reaches our catalog — because research deserves nothing less than complete confidence in what it studies.
          </p>
        </div>
      </section>
    </div>
  );
}
